import {Component, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {RoomSetting} from '../../models/room-setting';
import {GroupSetting} from '../../models/group-setting';
import {SharedValueService} from '../../services/shared-value.service';
import {RoomService} from '../../services/room.service';
import {GroupService} from '../../services/group.service';
import {DialogService} from '../../services/dialog.service';
import {DialogResult} from '../../models/dialog-param';
import {NotifierService} from 'angular-notifier';
import {DatatableComponent} from '@swimlane/ngx-datatable';
import {Helper} from '../../common/helper';
import {Subscription} from 'rxjs/Rx';
import {TypeRoom} from '../../common/constants';
import {SiteSetting} from '../../models/site-setting';
import {SiteService} from '../../services/site.service';

@Component({
  selector: 'app-room-group-page',
  templateUrl: './room-group-page.component.html',
  styleUrls: ['./room-group-page.component.scss'],
  preserveWhitespaces: false
})
export class RoomGroupPageComponent implements OnInit, OnDestroy {
  public TypeRoom: typeof TypeRoom = TypeRoom;
  @ViewChild(DatatableComponent) roomTable: DatatableComponent;
  @ViewChild(DatatableComponent) groupTable: DatatableComponent;
  @ViewChild(DatatableComponent) siteTable: DatatableComponent;
  originalRoomsList: RoomSetting[] = [];
  displayedRooms: RoomSetting[] = [];
  originalGroupsList: GroupSetting[] = [];
  originalSiteList: SiteSetting[] = [];
  displayedGroups: GroupSetting[] = [];
  displayedSites: SiteSetting[] = [];
  roomLoading = false;
  groupLoading = false;
  siteLoading = false;
  lengthMenu = [
    {value: 10, text: '10'},
    {value: 25, text: '25'},
    {value: 50, text: '50'},
    {value: -1, text: 'All'},
    ];
  roomPageSize = 10;
  groupPageSize = 10;
  sitePageSize = 10;
  roomSearch: string;
  groupSearch: string;
  siteSearch: string;
  roomSubscription: Subscription;
  groupSubscription: Subscription;
  siteSubscription: Subscription;

  constructor(private sharedValueService: SharedValueService,
              private roomService: RoomService,
              private groupService: GroupService,
              private siteService: SiteService,
              private dialog: DialogService,
              private notify: NotifierService,
              private helper: Helper) {
  }

  ngOnInit() {
    // get room data
    this.getRoomData();

    // get group data
    this.getGroupData();

    // get site data
    this.getSiteData();
  }

  showLoading(flag: boolean) {
    setTimeout(() => {
      this.dialog.setLoaderVisible(flag);
    });
  }

  showRoomItems(length) {
    const selectedRoomPageLength = parseInt(length, 10);
    if (selectedRoomPageLength !== -1) {
      this.roomPageSize = selectedRoomPageLength;
    } else {
      this.roomPageSize = this.displayedRooms.length;
    }
  }

  getRoomData(reload = false) {
    setTimeout(() => {
      this.roomLoading = true;
    });
    this.roomService.getAllRooms(reload).subscribe(rooms => {
      this.originalRoomsList = rooms;
      if (this.roomSearch !== '') {
        this.displayedRooms = this.filterRoomData([...this.originalRoomsList]);
      } else {
        this.displayedRooms = [...this.originalRoomsList];
      }
      setTimeout(() => {
        this.roomLoading = false;
      });
    }, error => {
      setTimeout(() => {
        this.roomLoading = false;
      });
    });
  }

  // search room
  updateRoomFilter(keyword) {
    this.roomLoading = true;
    this.roomSearch = keyword.trim().toLowerCase();
    if (this.roomSearch !== '') {
      const tempRoom = [...this.originalRoomsList];
      // filter our data & update the rows
      this.displayedRooms = this.filterRoomData(tempRoom);
    } else {
      this.displayedRooms = [...this.originalRoomsList];
    }
    this.roomLoading = false;
    // Whenever the filter changes, always go back to the first page
    this.roomTable.offset = 0;
  }

  // filter data
  filterRoomData(tempRoom: RoomSetting[]) {
    return tempRoom.filter((d) => {
      if (d.r_size) {
        return d.r_name.toLowerCase().indexOf(this.roomSearch) !== -1 ||
          d.r_size.toString().indexOf(this.roomSearch) !== -1 || !this.roomSearch;
      } else {
        return d.r_name.toLowerCase().indexOf(this.roomSearch) !== -1 || !this.roomSearch;
      }
    });
  }

  addRoom() {
    const newRoom = new RoomSetting();
    const obj = {
      fields: [
        {
        name: 'Room name',
        placeholder: 'Room name...',
        type: 'text',
        class: 'form-control fs13',
        key: 'r_name',
        isRequired: true
       },
       {
         name: 'Room size',
         placeholder: 'Room size...',
         type: 'number',
         class: 'form-control fs13',
         key: 'r_size',
         min: 0
       },
        {
          name: 'Site',
          type: 'select',
          class: 'form-control fs13',
          key: 'site_id',
          isRequired: true,
          options: this.originalSiteList
        },
       {
         name: 'Room color',
         placeholder: null,
         type: 'color',
         class: 'room_color ml-1 align-middle',
         key: 'color'
       },
        {
          name: 'Peripheral device',
          type: 'checkbox',
          class: 'ml-1 align-middle',
          key: 'peripheral_flag'
        },
        {
          name: 'Room Vip',
          type: 'checkbox',
          class: 'ml-1 align-middle',
          key: 'type_room'
        }
      ],
      isAPICallError: false,
      checkValidate: true,
      model: newRoom
    };
    this.dialog.showRoomGroupModal('Add Room', 'Add', 'Close', obj)
      .subscribe((res: DialogResult) => {
        if (res.isOk()) {
          const _model = Object.assign(new RoomSetting(), res.payload.model);
          _model.r_name = _model.r_name.trim();
          _model.r_size = _model.r_size && _model.r_size.trim() !== '' ? _model.r_size : null;
          this.roomService.postAddRoom(_model).subscribe(response => {
            this.sharedValueService.httpStatus.next(true);
            this.roomService.getAllRooms(true).subscribe();
            this.notify.notify('success', response.message);
            this.originalRoomsList.push(response.data);
            if (this.roomSearch !== '') {
              this.displayedRooms = this.filterRoomData([...this.originalRoomsList]);
            } else {
              this.displayedRooms = [...this.originalRoomsList];
            }
          }, error => {
            this.sharedValueService.httpStatus.next(false);
            const message = error.error.message ? error.error.message : 'Error occurred!';
            this.notify.notify('error', message);
          });
        }
    });
  }

  editRoom(room: RoomSetting) {
    if (room) {
      room.site_id = room.site_id ? room.site_id : '';
      const obj = {
        fields: [
          {
            name: 'Room name',
            placeholder: 'Room name...',
            type: 'text',
            class: 'form-control fs13',
            key: 'r_name',
            isRequired: true
          },
          {
            name: 'Room size',
            placeholder: 'Room size...',
            type: 'number',
            class: 'form-control fs13',
            key: 'r_size',
            min: 0
          },
          {
            name: 'Site',
            type: 'select',
            class: 'form-control fs13',
            key: 'site_id',
            isRequired: true,
            options: this.originalSiteList
          },
          {
            name: 'Room color',
            placeholder: null,
            type: 'color',
            class: 'room_color ml-1 align-middle',
            key: 'color'
          },
          {
            name: 'Peripheral device',
            type: 'checkbox',
            class: 'ml-1 align-middle',
            key: 'peripheral_flag'
          },
          {
            name: 'Room Vip',
            type: 'checkbox',
            class: 'ml-1 align-middle',
            key: 'type_room'
          }
        ],
        isAPICallError: false,
        checkValidate: true,
        model: Object.assign(new RoomSetting(), room)
      };
      this.dialog.showRoomGroupModal('Update Room', 'Update', 'Close', obj)
        .subscribe((res: DialogResult) => {
          if (res.isOk()) {
            const _model = Object.assign(new RoomSetting(), res.payload.model);
            _model.r_name = _model.r_name.trim();
            this.roomService.putUpdateRoom(_model).subscribe(response => {
              this.notify.notify('success', response.message ? response.message : '');
              this.sharedValueService.httpStatus.next(true);
              const index = this.originalRoomsList.findIndex(item => item.id === room.id);
              this.originalRoomsList[index] = response.data;
              if (this.roomSearch !== '') {
                const displayedIndex = this.displayedRooms.findIndex(item => item.id === room.id);
                this.displayedRooms[displayedIndex] = response.data;
              } else {
                this.displayedRooms = [...this.originalRoomsList];
              }
              this.roomService.data = this.originalRoomsList;
            }, error => {
              this.sharedValueService.httpStatus.next(false);
              const message = error.error.message ? error.error.message : 'Error occurred!';
              this.notify.notify('error', message);
            });
          }
        });
    }
  }

  deleteRoom(roomId: number) {
    if (roomId) {
      const html = '<div> All future meetings in this room will be deleted.</div>' +
        '<div> Are you sure delete this room?</div>';
      this.dialog.showMessage('Delete room', null, html, 'Delete', null, null,
        'Cancel').subscribe((res: DialogResult) => {
          if (res.isOk()) {
            this.showLoading(true);
            this.roomService.deleteRoom(roomId).subscribe(response => {
              this.showLoading(false);
              this.roomService.getAllRooms(true).subscribe();
              this.notify.notify('success', response.message ? response.message : '');
              this.originalRoomsList = this.originalRoomsList.filter(item => item.id !== roomId);
              if (this.roomSearch !== '') {
                this.displayedRooms = this.displayedRooms.filter(item => item.id !== roomId);
              } else {
                this.displayedRooms = [...this.originalRoomsList];
              }
              this.roomService.data = this.originalRoomsList;
            }, error => {
              this.showLoading(false);
              const message = error.error.message ? error.error.message : 'Error occurred!';
              this.notify.notify('error', message);
            });
          }
      });
    }
  }

  setEquipment(room: RoomSetting) {
    this.showLoading(true);
    const peripheral_flag = room.peripheral_flag === 0 ? 1 : 0;
    this.roomService.setPeripheral(room.id, peripheral_flag).subscribe(res => {
      this.showLoading(false);
      this.notify.notify('success', res.message);

      const index = this.originalRoomsList.findIndex(item => item.id === room.id);
      this.originalRoomsList[index].peripheral_flag = peripheral_flag;


      const displayedIndex = this.displayedRooms.findIndex(item => item.id === room.id);
      this.displayedRooms[displayedIndex].peripheral_flag = peripheral_flag;

    }, error => {
      this.showLoading(false);
      const message = error.error.message ? error.error.message : 'Error occurred!';
      this.notify.notify('error', message);
    });
  }

  setTypeRoom(roomId: number, typeRoom: TypeRoom) {
    this.showLoading(true);
    const data = {
      roomId: roomId,
      typeRoom: typeRoom
    }
    this.roomService.setTypeRoom(data).subscribe(
      (res) => {
        this.showLoading(false);
        this.notify.notify('success', res.message);

        const index = this.originalRoomsList.findIndex(item => item.id === roomId);
        this.originalRoomsList[index].type_room = typeRoom;


        const displayedIndex = this.displayedRooms.findIndex(item => item.id === roomId);
        this.displayedRooms[displayedIndex].type_room = typeRoom;
      },
      (error) => {
        this.showLoading(false);
        const message = error.error.message ? error.error.message : 'Error occurred!';
        this.notify.notify('error', message);
      }
    );
  }

  refreshRoomTable() {
    this.roomSearch = '';
    this.getRoomData(true);
  }

  /*------------------------------GROUP--------------------*/

  getGroupData(reload = false) {
    setTimeout(() => {
      this.groupLoading = true;
    });
    this.helper._unsubsribe(this.groupSubscription);
    this.groupService.getAllGroups(reload).subscribe(groups => {
      this.originalGroupsList = groups;
      this.displayedGroups = [...this.originalGroupsList];
      setTimeout(() => {
        this.groupLoading = false;
      });
    }, error => {
      setTimeout(() => {
        this.groupLoading = false;
      });
    });
  }

  showGroupItems(length) {
    const selectedGroupPageLength = parseInt(length, 10);
    if (selectedGroupPageLength !== -1) {
      this.groupPageSize = selectedGroupPageLength;
    } else {
      this.groupPageSize = this.displayedGroups.length;
    }
  }

  updateGroupFilter(keyword) {
    this.groupSearch = keyword.trim().toLowerCase();
    // filter our data && update the rows
    this.displayedGroups = this.filterGroupDate([...this.originalGroupsList]);
    // Whenever the filter changes, always go back to the first page
    this.groupTable.offset = 0;
  }

  filterGroupDate(tempGroup: GroupSetting[]) {
    return tempGroup.filter((d) => {
      return d.g_name.toLowerCase().indexOf(this.groupSearch) !== -1 || !this.groupSearch;
    });
  }

  addGroup() {
    const newGroup = new GroupSetting();
    const obj = {
      fields: [
        {
          name: 'Group name',
          placeholder: 'Group name...',
          type: 'text',
          class: 'form-control fs13',
          key: 'g_name',
          isRequired: true
        }
      ],
      isAPICallError: false,
      checkValidate: true,
      model: newGroup
    };
    this.dialog.showRoomGroupModal('Add Group', 'Add', 'Close', obj)
      .subscribe((res: DialogResult) => {
        if (res.isOk()) {
          const _model = Object.assign(new GroupSetting(), res.payload.model);
          _model.g_name = _model.g_name.trim();
          this.groupService.postAddGroup(_model).subscribe(response => {
            this.notify.notify('success', response.message);
            this.sharedValueService.httpStatus.next(true);
            this.groupService.getAllGroups(true).subscribe();
            this.originalGroupsList = [...this.originalGroupsList, response.data];
            this.groupService.data = this.originalGroupsList;
            if (this.groupSearch !== '') {
              this.displayedGroups = this.filterGroupDate([...this.originalGroupsList]);
            } else {
              this.displayedGroups = [...this.originalGroupsList];
            }
          }, error => {
            this.sharedValueService.httpStatus.next(false);
            const message = error.error.message ? error.error.message : 'Error occurred!';
            this.notify.notify('error', message);
          });
        }

      });
  }

  editGroup(group: GroupSetting) {
    if (group) {
      const obj = {
        fields: [
          {
            name: 'Group name',
            placeholder: 'Group name...',
            type: 'text',
            class: 'form-control fs13',
            key: 'g_name',
            isRequired: true
          }
        ],
        isAPICallError: false,
        checkValidate: true,
        model: Object.assign(new GroupSetting(), group)
      };
      this.dialog.showRoomGroupModal('Update Group', 'Update', 'Close', obj)
        .subscribe((res: DialogResult) => {
          if (res.isOk()) {
            const _model = Object.assign(new GroupSetting(), res.payload.model);
            _model.g_name = _model.g_name.trim();
            this.groupService.putUpdateGroup(_model).subscribe(response => {
              this.notify.notify('success', response.message ? response.message : '');
              this.sharedValueService.httpStatus.next(true);
              const index = this.originalGroupsList.findIndex(item => item.id === group.id);
              this.originalGroupsList[index] = response.data;
              this.groupService.data = this.originalGroupsList;
              if (this.groupSearch !== '') {
                const displayedIndex = this.displayedGroups.findIndex(item => item.id === group.id);
                this.displayedGroups[displayedIndex] = response.data;
              } else {
                this.displayedGroups = [...this.originalGroupsList];
              }
            }, error => {
              this.sharedValueService.httpStatus.next(false);
              const message = error.error.message ? error.error.message : 'Error occurred!';
              this.notify.notify('error', message);
            });
          }
        });
    }
  }

  deleteGroup(groupId: number) {
    if (groupId) {
      this.dialog.showMessage('Delete room', 'Are you sure delete this group?', null, 'Delete', null, null,
        'Cancel').subscribe((res: DialogResult) => {
        if (res.isOk()) {
          this.showLoading(true);
          this.groupService.deleteGroup(groupId).subscribe(response => {
            this.showLoading(false);
            this.originalGroupsList = this.originalGroupsList.filter(item => item.id !== groupId);
            this.groupService.getAllGroups(true).subscribe();
            if (this.groupSearch !== '') {
              this.displayedGroups = this.displayedGroups.filter(item => item.id !== groupId);
            } else {
              this.displayedGroups = [...this.originalGroupsList];
            }
            this.notify.notify('success', response.message ? response.message : '');
          }, error => {
            this.showLoading(false);
            const message = error.error.message ? error.error.message : 'Error occurred!';
            this.notify.notify('error', message);
          });
        }
      });
    }
  }

  refreshGroupTable() {
    this.groupSearch = '';
    this.getGroupData(true);
  }


  /*------------------------------Site--------------------*/
  getSiteData(reload = false) {
    setTimeout(() => {
      this.siteLoading = true;
    });
    this.helper._unsubsribe(this.siteSubscription);
    this.siteService.getAllSite(reload).subscribe(sites => {
      this.originalSiteList = sites;
      this.displayedSites = [...this.originalSiteList];
      setTimeout(() => {
        this.siteLoading = false;
      });
    }, error => {
      setTimeout(() => {
        this.siteLoading = false;
      });
    });
  }

  refreshSiteTable() {
    this.siteSearch = '';
    this.getSiteData(true);
  }

  showSiteItems(length) {
    const selectedSitePageLength = parseInt(length, 10);
    if (selectedSitePageLength !== -1) {
      this.sitePageSize = selectedSitePageLength;
    } else {
      this.sitePageSize = this.displayedSites.length;
    }
  }

  updateSiteFilter(keyword) {
    this.siteSearch = keyword.trim().toLowerCase();
    // filter our data && update the rows
    this.displayedSites = this.filterSiteData([...this.originalSiteList]);
    // Whenever the filter changes, always go back to the first page
    this.siteTable.offset = 0;
  }

  filterSiteData(tempSite: SiteSetting[]) {
    return tempSite.filter((d) => {
      return d.name.toLowerCase().indexOf(this.siteSearch) !== -1 || !this.siteSearch;
    });
  }

  initFormSite(model) {
    return {
      fields: [
        {
          name: 'Site name',
          placeholder: 'Site name...',
          type: 'text',
          class: 'form-control fs13',
          key: 'name',
          isRequired: true,
          maxlength: 50
        },
        {
          name: 'Site description',
          placeholder: 'Site description...',
          type: 'text',
          class: 'form-control fs13',
          key: 'description',
          maxlength: 255
        }
      ],
      isAPICallError: false,
      checkValidate: true,
      model: model
    };
  }

  addSite() {
    const newSite = new SiteSetting();
    const obj = this.initFormSite(newSite);
    this.dialog.showRoomGroupModal('Add Site', 'Add', 'Close', obj)
      .subscribe((res: DialogResult) => {
        if (res.isOk()) {
          const _model = Object.assign(new SiteSetting(), res.payload.model);
          _model.name = _model.name.trim();
          _model.description = _model.description.trim();
          this.siteService.addSite(_model).subscribe(response => {
            this.notify.notify('success', response.message);
            this.sharedValueService.httpStatus.next(true);
            this.siteService.getAllSite(true).subscribe();
            this.originalSiteList = [...this.originalSiteList, response.data];
            this.siteService.data = this.originalSiteList;
            if (this.siteSearch !== '') {
              this.displayedSites = this.filterSiteData([...this.originalSiteList]);
            } else {
              this.displayedSites = [...this.originalSiteList];
            }
          }, error => {
            this.sharedValueService.httpStatus.next(false);
            const message = error.error.message ? error.error.message : 'Error occurred!';
            this.notify.notify('error', message);
          });
        }
      });
  }

  editSite(site: SiteSetting) {
    const obj = this.initFormSite(Object.assign(new SiteSetting(), site));
    this.dialog.showRoomGroupModal('Update Site', 'Update', 'Close', obj)
      .subscribe((res: DialogResult) => {
        if (res.isOk()) {
          const _model = Object.assign(new SiteSetting(), res.payload.model);
          _model.name = _model.name.trim();
          _model.description = _model.description.trim();
          this.siteService.updateSite(_model).subscribe(response => {
            this.notify.notify('success', response.message ? response.message : '');
            this.sharedValueService.httpStatus.next(true);
            const index = this.originalSiteList.findIndex(item => item.id === site.id);
            this.originalSiteList[index] = response.data;
            this.siteService.data = this.originalSiteList;
            if (this.siteSearch !== '') {
              const displayedIndex = this.displayedSites.findIndex(item => item.id === site.id);
              this.displayedSites[displayedIndex] = response.data;
            } else {
              this.displayedSites = [...this.originalSiteList];
            }
          }, error => {
            this.sharedValueService.httpStatus.next(false);
            const message = error.error.message ? error.error.message : 'Error occurred!';
            this.notify.notify('error', message);
          });
        }
      });
  }

  deleteSite(siteId: number) {
    this.dialog.showMessage('Delete site', 'Are you sure delete this site?', null, 'Delete', null, null,
      'Cancel').subscribe((res: DialogResult) => {
      if (res.isOk()) {
        this.showLoading(true);
        this.siteService.deleteSite(siteId).subscribe(response => {
          this.showLoading(false);
          this.originalSiteList = this.originalSiteList.filter(item => item.id !== siteId);
          this.siteService.getAllSite(true).subscribe();
          this.siteService.data = this.originalSiteList;
          if (this.siteSearch !== '') {
            this.displayedSites = this.displayedSites.filter(item => item.id !== siteId);
          } else {
            this.displayedSites = [...this.originalSiteList];
          }

          // update rooms
          this.getRoomData(true);

          this.notify.notify('success', response.message ? response.message : '');
        }, error => {
          this.showLoading(false);
          const message = error.error.message ? error.error.message : 'Error occurred!';
          this.notify.notify('error', message);
        });
      }
    });
  }

  ngOnDestroy() {
    this.helper._unsubsribe(this.groupSubscription);
    this.helper._unsubsribe(this.roomSubscription);
    this.helper._unsubsribe(this.siteSubscription);
  }
}



// WEBPACK FOOTER //
// /app/src/app/pages/room-group-page/room-group-page.component.ts